import { AlertCircle } from "lucide-react";
import type { TApiFileStatus } from "@/apis/proda_finance/types";
import { formatDateTimeEuropeBerlin } from "@/utils/europeBerlinTime";

const REJECTED_BY_PRODA = "Rejected by PRODA";

export type TValidationOutcomeFile = {
    status: TApiFileStatus;
    rejectionReason: string | null;
    rejectedById: string | null;
    rejectedByName: string | null;
    rejectedAt: string | null;
};

type TValidationOutcomePanelProps = {
    file: TValidationOutcomeFile;
};

function formatTimestamp(value: string | null): string | null {
    if (!value) return null;
    const formatted = formatDateTimeEuropeBerlin(value);
    return formatted || null;
}

export function ValidationOutcomePanel({ file }: TValidationOutcomePanelProps) {
    if (file.status === "failed") {
        return (
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm" role="status">
                <div className="flex gap-3">
                    <AlertCircle className="h-5 w-5 shrink-0 text-blue-700" aria-hidden />
                    <div className="min-w-0 space-y-2">
                        <p className="font-medium text-blue-900">
                            We hit a problem processing this file.
                        </p>
                        <p className="text-blue-900/80">
                            This isn&apos;t something you need to fix. Our team is investigating and
                            will move the file forward once the issue is resolved.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (file.status === "rejected") {
        const timestamp = formatTimestamp(file.rejectedAt);
        const isManual = Boolean(file.rejectedById);
        const title = isManual ? `Rejected by ${file.rejectedByName || "user"}` : REJECTED_BY_PRODA;
        const reason = file.rejectionReason || "This file did not pass validation.";

        return (
            <div
                data-testid="rejection-banner"
                className="rounded-xl border border-destructive/20 bg-destructive/5 p-4 text-sm"
                role="alert"
            >
                <div className="flex gap-3">
                    <AlertCircle className="h-5 w-5 shrink-0 text-destructive" aria-hidden />
                    <div className="min-w-0 space-y-2">
                        <p className="font-medium text-destructive">{title}</p>
                        {timestamp && <p className="text-muted-foreground">{timestamp}</p>}
                        <p>{reason}</p>
                        <p className="text-muted-foreground">
                            Please correct the source file and re-upload.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return null;
}
